import {Component, OnInit, AfterViewInit, OnDestroy, ViewChildren, ElementRef} from '@angular/core';
import {FormBuilder, FormGroup, FormControl, FormArray, Validators, FormControlName} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';

import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/observable/fromEvent';
import 'rxjs/add/observable/merge';
import {Observable} from 'rxjs/Observable';
import {Subscription} from 'rxjs/Subscription';

import {ICustomer} from '../customer';
import {GenericValidator} from '../../../shared/generic-validator';
import {CustomerService} from '../../../services/customer.service';
import {Title} from '@angular/platform-browser';


@Component({
    templateUrl: './customer-edit.component.html',
    styleUrls: ['./customer-edit.component.scss'],
    providers: [CustomerService]
})
export class CustomerEditComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren(FormControlName, {read: ElementRef}) formInputElements: ElementRef[];

    pageTitle = '';
    errorMessage: string;
    customerForm: FormGroup;

    customer: ICustomer;
    private sub: Subscription;


    // Use with the generic validation message class
    displayMessage: { [key: string]: string } = {};
    private validationMessages: { [key: string]: { [key: string]: string } };
    private genericValidator: GenericValidator;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private customerService: CustomerService,
        private title: Title
    ) {

        // Defines all of the validation messages for the form.
        // These could instead be retrieved from a file or database.
        this.validationMessages = {
            CU1_NAME: {
                required: 'customer name is required.',
                minlength: 'customer name must be at least three characters.',
                maxlength: 'customer name cannot exceed 50 characters.'
            },
        };

        // Define an instance of the validator for use with this form,
        // passing in this form's set of validation messages.
        this.genericValidator = new GenericValidator(this.validationMessages);

        this.title.setTitle('EDI - Customers');
    }

    ngOnInit(): void {
        this.customerForm = this.fb.group({
            CU1_ID: [''],
            CORP_ADDRESS_ID: [''],
            CU1_NAME: [''],
            CU1_CREATED_BY: [''],
            CU1_CREATED_ON: [''],
            CU1_MODIFIED_BY: [''],
            CU1_MODIFIED_ON: [''],
            CU1_SHOW_DEFAULT: [''],
            CU1_TEST_MODE: [''],
            CU1_COMPANY_ID: [''],
            CU1_RECEIVE_EDI: [''],
            CU1_SEND_EDI_INVOICES: [''],
            CU1_SEND_EDI_ASN: [''],
            CU1_SEND_EDI_ORDERS: [''],
            CU1_SEND_EDI_ORDER_CONFIRMATIONS: [''],
            CU1_SEND_ACKNOWLEDGEMENT: [''],
            CU1_ORDER_TYPE: [''],
            CU1_ORDER_FORMAT: [''],
            CU1_INVOICE_FORMAT: [''],
            CU1_ASN_FORMAT: [''],
            CU1_TXT_APPROVED: [''],
            CU1_SEND_FTP: [''],
            CU1_SEND_SFTP: [''],
            CU1_POST_HTTP: [''],
            CU1_RECEIVE_FTP: [''],
            CU1_PICKUP_FTP: [''],
            CU1_RECEIVE_HTTP: [''],
            CU1_PICKUP_SFTP: [''],
            CU1_REMOTE_FTP_SERVER: [''],
            CU1_REMOTE_FTP_USERNAME: [''],
            CU1_REMOTE_FTP_PASSWORD: [''],
            CU1_REMOTE_FTP_DIRECTORY_SEND: [''],
            CU1_REMOTE_FTP_DIRECTORY_PICKUP: [''],
            CU1_FTP_USER: [''],
            CU1_FTP_PASSWORD: [''],
            CU1_FTP_DIRECTORY: [''],
            CU1_REMOTE_HTTP_SERVER: [''],
            CU1_REMOTE_HTTP_USERNAME: [''],
            CU1_REMOTE_HTTP_PASSWORD: [''],
            CU1_SUPPLIER_CODE: [''],
            CU1_RECEIVER_QUALIFIER: [''],
            CU1_RECEIVER_ID: [''],
            CU1_FACILITY: [''],
            CU1_TRADING_PARTNER_QUALIFIER: [''],
            CU1_TRADING_PARTNER_ID: [''],
            CU1_TRADING_PARTNER_GS_ID: [''],
            CU1_ASN_TRADING_PARTNER_ID: [''],
            CU1_CONSOLIDATE_ASN: [''],
            CU1_FLAG: [''],
            CU1_X12_STANDARD: [''],
            CU1_EDI_VERSION: [''],
            CU1_DUNS: [''],
            CU1_SHARED_SECRET: [''],
            CU1_REJECT_INVALID_ITEM_ORDERS: [''],
            CU1_INVALID_ITEM_SUBSTITUTE: [''],
            CU1_USE_CONTRACT: [''],
            CU1_SEND_CUSTOMERS_AND_ITEMS: [''],
            CU1_STOP_IMPORT_WITH_ERRORS: [''],
            CU1_USE_CLASS_ID: [''],
            CU1_CLASS_ID: [''],
            CU1_MAP: [''],
            CU1_ORDER_PRICE_OVERRIDE: [''],
            CU1_SEND_CREDIT_INVOICES: [''],
            CU1_ONLY_SEND_CREDIT_INVOICES: [''],
            CU1_SEND_PAID_INVOICES: [''],
            CU1_852_IMPORT_FOLDER: [''],
            CU1_ALWAYS_SEND_ORDER_CONFIRMATIONS: [''],
            CU1_COMPLETE_SHIP_TO_NAME: [''],
            CU1_ALWAYS_SEND_ASNS: [''],
            CU1_IMPORT_FREIGHT_CODES: [''],
            CU1_POST_AS2: [''],
            CU1_RECEIVE_AS2: [''],
            CU1_CXML_PAYLOAD_ID: [''],
            CU1_AS2_CERTIFICATE_FILENAME: [''],
            CU1_AS2_RECEIVER_ID: [''],
            CU1_AS2_TRADING_PARTNER_ID: [''],
            CU1_USE_P21_SHIP_TO_DATA: [''],
            CU1_CUSTOMER_SENDS_P21_SHIP_TO_ID: [''],
            CU1_ALLOW_DUPLICATE_PO_NUMBERS: [''],
            CU1_ASN_IMPORT_FOLDER: [''],
            CU1_PICKUP_DIRECTORY: [''],
            CU1_SEND_INVENTORY: [''],
            CU1_INVENTORY_FORMAT: [''],
            CU1_AS2_REQUEST_RECEIPT: [''],
            CU1_AS2_SIGN_MESSAGES: [''],
            CU1_AS2_KEY_LENGTH: [''],
            CU1_AS2_ENCRYPTION_ALGORITHM: [''],
            CU1_AS2_COMPATIBILITY_MODE: [''],
            CU1_SEND_PRICE_CATALOG: [''],
            CU1_PRICE_CATALOG_FORMAT: [''],
            CU1_ORDER_FORWARDING_FOLDER: [''],
            CU1_PRICE_CATALOG_IMPORT_FOLDER: [''],
            CU1_INVOICE_IMPORT_FOLDER: [''],
            CU1_PICKUP_EBAY: [''],
            CU1_EBAY_TOKEN: [''],
            CU1_EBAY_DEV_NAME: [''],
            CU1_EBAY_APP_NAME: [''],
            CU1_EBAY_CERT_NAME: [''],
            CU1_SEND_EDI_PICK_TICKETS: [''],
            CU1_PICK_TICKET_FORMAT: [''],
            CU1_PICK_TICKET_LOCATION_ID: [''],
            CU1_SAVE_867_IN_DATABASE: [''],
            CU1_SEND_ORDER_CHECK: [''],
            CU1_ORDER_CHECK_EMAIL: [''],
            CU1_SHIP_TO_FILTER: [''],
            CU1_REMOTE_FTP_DIRECTORY_SEND_INVENTORY: [''],
            CU1_PICKUP_AMAZON: [''],
            CU1_AMAZON_MWS_MARKET_ID_US: [''],
            CU1_AWS_ACCESS_KEY: [''],
            CU1_AWS_SECRET_KEY: [''],
            CU1_AWS_SELLER_ID: [''],
            CU1_AWS_MARKETPLACE_ID: [''],
            CU1_AWS_UTC_HOUR_OFFSET: [''],
            CU1_PICKUP_BILLTRUST: [''],
            CU1_BILLTRUST_DEVELOPER_GUID: [''],
            CU1_BILLTRUST_CLIENT_GUID: [''],
            CU1_QUOTE_FORMAT: [''],
            CU1_SEND_EDI_QUOTES: [''],
            CU1_FTP_DOWNLOAD_FILTER: [''],
            CU1_CHECK_ACKNOWLEDGEMENTS: [''],
            CU1_830_FORWARDING_FOLDER: [''],
            CU1_PICKUP_CHANNEL_ADVISOR: [''],
            CU1_CHANNEL_ADVISOR_DEV_KEY: [''],
            CU1_CHANNEL_ADVISOR_PASSWORD: [''],
            CU1_CHANNEL_ADVISOR_ACCOUNT_ID: [''],
            CU1_CHANNEL_ADVISOR_LOCAL_ID: [''],
            CU1_SFTP_PRIVATE_KEY_FILENAME: [''],
            CU1_ALWAYS_SEND_INVOICES: [''],
            CU1_QUOTE_IMPORT_FOLDER: [''],
            CU1_SEND_SINGLE_LINE_INVOICES: [''],
            CU1_INVOICE_FORWARDING_FOLDER: [''],
            CU1_PICKUP_EMAIL: [''],
            CU1_EMAIL_SERVER: [''],
            CU1_EMAIL_USER: [''],
            CU1_EMAIL_PASSWORD: [''],
            CU1_EMAIL_PORT: [''],
            CU1_EMAIL_REQUIRES_TLS: [''],
            CU1_PICKUP_SELLER_CLOUD: [''],
            CU1_SELLER_CLOUD_USERNAME: [''],
            CU1_SELLER_CLOUD_PASSWORD: [''],
            CU1_SELLER_CLOUD_PICKUP_FBA: [''],
            CU1_ASN_FORWARDING_FOLDER: [''],
            CU1_SEND_CONTRACT_ORDERS: [''],
            CU1_SEND_EDI_TRANSFERS: [''],
            CU1_TRANSFER_FORMAT: [''],
            CU1_TRANSFER_LOCATION_ID: [''],
            CU1_SEND_INVENTORY_RECEIPT_ORDERS: [''],
            CU1_SEND_EDI_ROUTING_REQUESTS: [''],
            CU1_ROUTING_REQUEST_FORMAT: [''],
            CU1_PICKUP_MAGENTO: [''],
            CU1_MAGENTO_VERSION: [''],
            CU1_MAGENTO_USERNAME: [''],
            CU1_MAGENTO_PASSWORD: [''],
            CU1_MAGENTO_API_KEY: [''],
            CU1_ORDER_CONFIRMATION_IMPORT_FOLDER: [''],
            CU1_RECEIVE_API_CALLS: [''],
            CU1_API_USERNAME: [''],
            CU1_API_PASSWORD: [''],
            CU1_PICKUP_CTL_SHIPMENTS: [''],
            CU1_CTL_CLIENT_ID: [''],
            CU1_CTL_USER_ID: [''],
            CU1_IMPORT_SCHEDULED_RELEASES_INTO_P21: [''],
            CU1_PICKUP_CTL_RMA_INFO: [''],
            CU1_SYNCHRONIZED: [''],
            CU1_PICKUP_BIG_COMMERCE: [''],
            CU1_BIG_COMMERCE_ACCESS_TOKEN: [''],
            CU1_BIG_COMMERCE_CLIENT_ID: [''],
            CU1_BIG_COMMERCE_CLIENT_SECRET: [''],
            CU1_REMOTE_ORDER_DATABASE: [''],
            CU1_REMOTE_ORDER_TABLE: [''],
            CU1_CUSTOMER_LIST_URL: [''],

        });

        // Read the customer Id from the route parameter
        this.sub = this.route.params.subscribe(
            params => {
                this.getCustomer(params['id'], params['CO1_ID']);
            }
        );

    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    ngAfterViewInit(): void {
        // Watch for the blur event from any input element on the form.
        const controlBlurs: Observable<any>[] = this.formInputElements
            .map((formControl: ElementRef) => Observable.fromEvent(formControl.nativeElement, 'blur'));

        // Merge the blur event observable with the valueChanges observable
        Observable.merge(this.customerForm.valueChanges, ...controlBlurs).debounceTime(800).subscribe(value => {
            this.displayMessage = this.genericValidator.processMessages(this.customerForm);
        });
    }

    getCustomer(id: number, CO1_ID: number): void {
        this.customerService.getById(id, CO1_ID)
            .subscribe(
                (customer: ICustomer) => this.onCustomerRetrieved(customer),
                (error: any) => this.errorMessage = <any>error
            );
    }

    onCustomerRetrieved(customer: ICustomer): void {
        if (this.customerForm) {
            this.customerForm.reset();
        }

        this.customer = customer[0] || customer;

        if (this.customer.CU1_ID === 0) {
            this.pageTitle = 'Add customer';
        } else {
            this.pageTitle = `Customer: ${this.customer.CU1_NAME}`;
        }

        // Update the data on the form
        this.customerForm.patchValue({
            CU1_ID: this.customer.CU1_ID,
            CORP_ADDRESS_ID: this.customer.CORP_ADDRESS_ID,
            CU1_NAME: this.customer.CU1_NAME,
            CU1_RECEIVER_QUALIFIER: this.customer.CU1_RECEIVER_QUALIFIER,
            CU1_RECEIVER_ID: this.customer.CU1_RECEIVER_ID,
            CU1_TRADING_PARTNER_QUALIFIER: this.customer.CU1_TRADING_PARTNER_QUALIFIER,
            CU1_TRADING_PARTNER_ID: this.customer.CU1_TRADING_PARTNER_ID,
            CU1_POST_AS2: this.customer.CU1_POST_AS2,
            CU1_RECEIVE_AS2: this.customer.CU1_RECEIVE_AS2,
            CU1_AS2_CERTIFICATE_FILENAME: this.customer.CU1_AS2_CERTIFICATE_FILENAME,
            CU1_FLAG: this.customer.CU1_FLAG,
            CU1_EDI_VERSION: this.customer.CU1_EDI_VERSION,
            CU1_X12_STANDARD: this.customer.CU1_X12_STANDARD,
            CU1_DUNS: this.customer.CU1_DUNS,
            CU1_SUPPLIER_CODE: this.customer.CU1_SUPPLIER_CODE,
            CU1_FACILITY: this.customer.CU1_FACILITY,
            CU1_ASN_TRADING_PARTNER_ID: this.customer.CU1_ASN_TRADING_PARTNER_ID,
            CU1_MAP: this.customer.CU1_MAP,
            CU1_852_IMPORT_FOLDER: this.customer.CU1_852_IMPORT_FOLDER,
            CU1_SEND_EDI_INVOICES: this.customer.CU1_SEND_EDI_INVOICES,
            CU1_SEND_EDI_ORDERS: this.customer.CU1_SEND_EDI_ORDERS,
            CU1_SEND_EDI_ASN: this.customer.CU1_SEND_EDI_ASN,
            CU1_SEND_INVENTORY: this.customer.CU1_SEND_INVENTORY,
            CU1_SEND_CREDIT_INVOICES: this.customer.CU1_SEND_CREDIT_INVOICES,
            CU1_USE_CLASS_ID: this.customer.CU1_USE_CLASS_ID,
            CU1_SEND_EDI_ORDER_CONFIRMATIONS: this.customer.CU1_SEND_EDI_ORDER_CONFIRMATIONS,
            CU1_ALWAYS_SEND_ORDER_CONFIRMATIONS: this.customer.CU1_ALWAYS_SEND_ORDER_CONFIRMATIONS,
            CU1_CONSOLIDATE_ASN: this.customer.CU1_CONSOLIDATE_ASN,
            CU1_ALWAYS_SEND_ASNS: this.customer.CU1_ALWAYS_SEND_ASNS,
            CU1_CLASS_ID: this.customer.CU1_CLASS_ID,
            CU1_INVOICE_FORMAT: this.customer.CU1_INVOICE_FORMAT,
            CU1_ASN_FORMAT: this.customer.CU1_ASN_FORMAT,
            CU1_INVENTORY_FORMAT: this.customer.CU1_INVENTORY_FORMAT,
            CU1_ORDER_FORMAT: this.customer.CU1_ORDER_FORMAT,
            CU1_SEND_FTP: this.customer.CU1_SEND_FTP,
            CU1_SEND_SFTP: this.customer.CU1_SEND_SFTP,
            CU1_REMOTE_FTP_SERVER: this.customer.CU1_REMOTE_FTP_SERVER,
            CU1_REMOTE_FTP_DIRECTORY_SEND: this.customer.CU1_REMOTE_FTP_DIRECTORY_SEND,
            CU1_REMOTE_FTP_DIRECTORY_PICKUP: this.customer.CU1_REMOTE_FTP_DIRECTORY_PICKUP,
            CU1_REMOTE_FTP_USERNAME: this.customer.CU1_REMOTE_FTP_USERNAME,
            CU1_REMOTE_FTP_PASSWORD: this.customer.CU1_REMOTE_FTP_PASSWORD,
            CU1_POST_HTTP: this.customer.CU1_POST_HTTP,
            CU1_AS2_REQUEST_RECEIPT: this.customer.CU1_AS2_REQUEST_RECEIPT,
            CU1_AS2_SIGN_MESSAGES: this.customer.CU1_AS2_SIGN_MESSAGES,
            CU1_REMOTE_HTTP_SERVER: this.customer.CU1_REMOTE_HTTP_SERVER,
            CU1_AS2_KEY_LENGTH: this.customer.CU1_AS2_KEY_LENGTH,
            CU1_PICKUP_FTP: this.customer.CU1_PICKUP_FTP,
            CU1_FTP_USER: this.customer.CU1_FTP_USER,
            CU1_FTP_PASSWORD: this.customer.CU1_FTP_PASSWORD,
            CU1_FTP_DIRECTORY: this.customer.CU1_FTP_DIRECTORY,
            CU1_RECEIVE_EDI: this.customer.CU1_RECEIVE_EDI,
            CU1_SEND_ACKNOWLEDGEMENT: this.customer.CU1_SEND_ACKNOWLEDGEMENT,
            CU1_REJECT_INVALID_ITEM_ORDERS: this.customer.CU1_REJECT_INVALID_ITEM_ORDERS,
            CU1_INVALID_ITEM_SUBSTITUTE: this.customer.CU1_INVALID_ITEM_SUBSTITUTE,
            CU1_USE_CONTRACT: this.customer.CU1_USE_CONTRACT,
            CU1_STOP_IMPORT_WITH_ERRORS: this.customer.CU1_STOP_IMPORT_WITH_ERRORS,
            CU1_USE_P21_SHIP_TO_DATA: this.customer.CU1_USE_P21_SHIP_TO_DATA,
            CU1_CUSTOMER_SENDS_P21_SHIP_TO_ID: this.customer.CU1_CUSTOMER_SENDS_P21_SHIP_TO_ID,
            CU1_ORDER_PRICE_OVERRIDE: this.customer.CU1_ORDER_PRICE_OVERRIDE,
            CU1_COMPLETE_SHIP_TO_NAME: this.customer.CU1_COMPLETE_SHIP_TO_NAME,
            CU1_ALLOW_DUPLICATE_PO_NUMBERS: this.customer.CU1_ALLOW_DUPLICATE_PO_NUMBERS,
            CU1_IMPORT_FREIGHT_CODES: this.customer.CU1_IMPORT_FREIGHT_CODES,
            CU1_ASN_IMPORT_FOLDER: this.customer.CU1_ASN_IMPORT_FOLDER,
            CU1_PICKUP_SFTP: this.customer.CU1_PICKUP_SFTP,
            CU1_TEST_MODE: this.customer.CU1_TEST_MODE,
            CU1_REMOTE_HTTP_USERNAME: this.customer.CU1_REMOTE_HTTP_USERNAME,
            CU1_REMOTE_HTTP_PASSWORD: this.customer.CU1_REMOTE_HTTP_PASSWORD,
            CU1_ONLY_SEND_CREDIT_INVOICES: this.customer.CU1_ONLY_SEND_CREDIT_INVOICES,
            CU1_SEND_PAID_INVOICES: this.customer.CU1_SEND_PAID_INVOICES,
            CU1_ALWAYS_SEND_INVOICES: this.customer.CU1_ALWAYS_SEND_INVOICES,
            CU1_PICKUP_DIRECTORY: this.customer.CU1_PICKUP_DIRECTORY,
            CU1_AS2_ENCRYPTION_ALGORITHM: this.customer.CU1_AS2_ENCRYPTION_ALGORITHM,
            CU1_AS2_COMPATIBILITY_MODE: this.customer.CU1_AS2_COMPATIBILITY_MODE,
            CU1_SEND_PRICE_CATALOG: this.customer.CU1_SEND_PRICE_CATALOG,
            CU1_PRICE_CATALOG_FORMAT: this.customer.CU1_PRICE_CATALOG_FORMAT,
            CU1_ORDER_FORWARDING_FOLDER: this.customer.CU1_ORDER_FORWARDING_FOLDER,
            CU1_PRICE_CATALOG_IMPORT_FOLDER: this.customer.CU1_PRICE_CATALOG_IMPORT_FOLDER,
            CU1_INVOICE_IMPORT_FOLDER: this.customer.CU1_INVOICE_IMPORT_FOLDER,
            CU1_PICKUP_EBAY: this.customer.CU1_PICKUP_EBAY,
            CU1_EBAY_TOKEN: this.customer.CU1_EBAY_TOKEN,
            CU1_EBAY_DEV_NAME: this.customer.CU1_EBAY_DEV_NAME,
            CU1_EBAY_APP_NAME: this.customer.CU1_EBAY_APP_NAME,
            CU1_EBAY_CERT_NAME: this.customer.CU1_EBAY_CERT_NAME,
            CU1_SEND_EDI_PICK_TICKETS: this.customer.CU1_SEND_EDI_PICK_TICKETS,
            CU1_PICK_TICKET_FORMAT: this.customer.CU1_PICK_TICKET_FORMAT,
            CU1_PICK_TICKET_LOCATION_ID: this.customer.CU1_PICK_TICKET_LOCATION_ID,
            CU1_SAVE_867_IN_DATABASE: this.customer.CU1_SAVE_867_IN_DATABASE,
            CU1_SEND_ORDER_CHECK: this.customer.CU1_SEND_ORDER_CHECK,
            CU1_SHIP_TO_FILTER: this.customer.CU1_SHIP_TO_FILTER,
            CU1_REMOTE_FTP_DIRECTORY_SEND_INVENTORY: this.customer.CU1_REMOTE_FTP_DIRECTORY_SEND_INVENTORY,
            CU1_PICKUP_AMAZON: this.customer.CU1_PICKUP_AMAZON,
            CU1_AMAZON_MWS_MARKET_ID_US: this.customer.CU1_AMAZON_MWS_MARKET_ID_US,
            CU1_AWS_ACCESS_KEY: this.customer.CU1_AWS_ACCESS_KEY,
            CU1_AWS_SECRET_KEY: this.customer.CU1_AWS_SECRET_KEY,
            CU1_AWS_SELLER_ID: this.customer.CU1_AWS_SELLER_ID,
            CU1_AWS_MARKETPLACE_ID: this.customer.CU1_AWS_MARKETPLACE_ID,
            CU1_AWS_UTC_HOUR_OFFSET: this.customer.CU1_AWS_UTC_HOUR_OFFSET,
            CU1_PICKUP_BILLTRUST: this.customer.CU1_PICKUP_BILLTRUST,
            CU1_BILLTRUST_DEVELOPER_GUID: this.customer.CU1_BILLTRUST_DEVELOPER_GUID,
            CU1_BILLTRUST_CLIENT_GUID: this.customer.CU1_BILLTRUST_CLIENT_GUID,
            CU1_SEND_EDI_QUOTES: this.customer.CU1_SEND_EDI_QUOTES,
            CU1_QUOTE_FORMAT: this.customer.CU1_QUOTE_FORMAT,
            CU1_FTP_DOWNLOAD_FILTER: this.customer.CU1_FTP_DOWNLOAD_FILTER,
            CU1_CHECK_ACKNOWLEDGEMENTS: this.customer.CU1_CHECK_ACKNOWLEDGEMENTS,
            CU1_830_FORWARDING_FOLDER: this.customer.CU1_830_FORWARDING_FOLDER,
            CU1_PICKUP_CHANNEL_ADVISOR: this.customer.CU1_PICKUP_CHANNEL_ADVISOR,
            CU1_CHANNEL_ADVISOR_DEV_KEY: this.customer.CU1_CHANNEL_ADVISOR_DEV_KEY,
            CU1_CHANNEL_ADVISOR_PASSWORD: this.customer.CU1_CHANNEL_ADVISOR_PASSWORD,
            CU1_CHANNEL_ADVISOR_LOCAL_ID: this.customer.CU1_CHANNEL_ADVISOR_LOCAL_ID,
            CU1_SFTP_PRIVATE_KEY_FILENAME: this.customer.CU1_SFTP_PRIVATE_KEY_FILENAME,
            CU1_SEND_SINGLE_LINE_INVOICES: this.customer.CU1_SEND_SINGLE_LINE_INVOICES,
        });

    }

    deleteCustomer(): void {
        if (this.customer.CU1_ID === 0) {
            // Don't delete, it was never saved.
            this.onSaveComplete();
        } else {
            if (confirm(`Really delete the customer: ${this.customer.CU1_NAME}?`)) {
                this.customerService.delete(this.customer.CU1_ID)
                    .subscribe(
                        () => this.onSaveComplete(),
                        (error: any) => this.errorMessage = <any>error
                    );
            }
        }
    }

    saveCustomer(): void {
        if (this.customerForm.dirty && this.customerForm.valid) {
            // Copy the form values over the customer object values
            const c = Object.assign({}, this.customer, this.customerForm.value);

            this.customerService.save(c, this.pageTitle === 'Add customer')
                .subscribe(
                    () => this.onSaveComplete(),
                    (error: any) => this.errorMessage = <any>error
                );
        } else if (!this.customerForm.dirty) {
            this.onSaveComplete();
        }
    }

    onSelect(id) {

        console.log(id)
    }

    onSaveComplete(): void {
        // Reset the form to clear the flags
        this.customerForm.reset();
        this.router.navigate(['customers']);
    }

    onCancel() {
        this.router.navigate( ['customers']);
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/customers/customer-edit/customer-edit.component.ts